import { create } from 'zustand';
import * as THREE from 'three';

import useEditorStore from './useEditorStore';

const DEFAULT_INTENSITY = 0.4; // 0‒1, shown as a percentage in the card

/** Run the Laplacian sweep in a worker; resolves with the smoothed positions. */
function smoothInWorker({ positions, triIndex, vertexCount, intensity, lockVolume }) {
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL('../lib/smoothWorker.js', import.meta.url), {
      type: 'module',
    });
    worker.onmessage = (e) => {
      worker.terminate();
      if (e.data.error) reject(new Error(e.data.error));
      else resolve(e.data.positions);
    };
    worker.onerror = (err) => {
      worker.terminate();
      reject(err instanceof Error ? err : new Error('smoothing worker error'));
    };
    const transfer = [positions.buffer];
    if (triIndex) transfer.push(triIndex.buffer);
    worker.postMessage(
      { positions, triIndex, vertexCount, opts: { intensity, lockVolume } },
      transfer,
    );
  });
}

/** Immutably set one tool's entry in a `…ByTool` map. */
function withTool(map, toolId, value) {
  return { ...map, [toolId]: value };
}

/**
 * State for the Global Smoothing tool (Prepare Scan and Flares).
 *
 * Unlike the zone Smoothing tool, this one takes no painted mask — it relaxes
 * the whole surface at once, so the card is just an intensity slider plus a
 * volume lock. The heavy sweep runs in a worker and therefore cannot happen
 * inside `execCommand` (which is synchronous): `prepareCommand()` does the work
 * up front and parks the finished command, then the tool registry hands it to
 * the editor history via `takePendingCommand()` — the same shape the locking
 * attachment tool uses.
 *
 * The same card is offered on more than one step, and each instance is applied
 * and undone on its own (separate undo snapshots, separate step checkpoints), so
 * everything that describes an *apply* is keyed by tool id. The slider settings
 * are deliberately not keyed — they carry over as a user preference.
 */
const useGlobalSmoothStore = create((set, get) => ({
  intensity: DEFAULT_INTENSITY,
  setIntensity: (intensity) => set({ intensity }),

  /** Hold per-station girth and length while smoothing (not a uniform scale). */
  lockVolume: true,
  setLockVolume: (lockVolume) => set({ lockVolume }),

  isSmoothing: false,

  /** toolId → whether that step's global smoothing is currently applied. */
  appliedByTool: {},
  setIsGlobalSmoothApplied: (toolId, status) =>
    set((s) => ({ appliedByTool: withTool(s.appliedByTool, toolId, status) })),

  /** toolId → applied state that came back from a draft rather than this session. */
  appliedFromDraftByTool: {},
  setIsGlobalSmoothAppliedFromDraft: (toolId, status) =>
    set((s) => ({
      appliedFromDraftByTool: withTool(s.appliedFromDraftByTool, toolId, status),
    })),

  /** toolId → the undoable command of that step's apply. */
  commandByTool: {},
  setCurrentGlobalSmoothCommand: (toolId, command) =>
    set((s) => ({ commandByTool: withTool(s.commandByTool, toolId, command) })),

  pendingCommand: null,

  /**
   * Smooth the live surface in a worker and park the resulting undoable command.
   * @param {string} toolId — the step's global-smoothing tool id, so the command
   *   flips the applied flag of the card the user actually pressed.
   * @returns {Promise<boolean>} whether a command is ready to execute
   */
  prepareCommand: async (toolId) => {
    if (get().isSmoothing) return false;

    const { sceneRefs, replaceShadedMesh } = useEditorStore.getState();
    const originalMesh = sceneRefs?.shadedMesh;
    const posAttr = originalMesh?.geometry?.getAttribute('position');
    if (!posAttr) return false;

    const { intensity, lockVolume } = get();
    const originalMaterial = sceneRefs.material;
    const originalMatrix = originalMesh.matrix.clone();
    // `replaceShadedMesh` disposes the mesh it swaps out, so undo restores from
    // a clone taken here rather than from the live geometry.
    const originalGeometry = originalMesh.geometry.clone();

    set({ isSmoothing: true });
    try {
      const index = originalMesh.geometry.getIndex();
      const smoothedPositions = await smoothInWorker({
        positions: Float32Array.from(posAttr.array), // buffer transferred to worker
        triIndex: index ? index.array.slice() : null,
        vertexCount: posAttr.count,
        intensity,
        lockVolume,
      });

      const smoothedGeometry = originalMesh.geometry.clone();
      smoothedGeometry.getAttribute('position').array.set(smoothedPositions);
      smoothedGeometry.getAttribute('position').needsUpdate = true;
      smoothedGeometry.deleteAttribute('normal'); // recompute from the new positions
      smoothedGeometry.computeVertexNormals();
      smoothedGeometry.computeBoundingBox();
      smoothedGeometry.computeBoundingSphere();

      const buildMesh = (geometry) => {
        const mesh = new THREE.Mesh(geometry, originalMaterial);
        mesh.matrix.copy(originalMatrix);
        // Decompose matrix into TRS and enable auto-updates for proper transform handling
        mesh.matrix.decompose(mesh.position, mesh.quaternion, mesh.scale);
        mesh.matrixAutoUpdate = true;
        return mesh;
      };

      set({
        pendingCommand: {
          execute: () => {
            replaceShadedMesh(buildMesh(smoothedGeometry));
            get().setIsGlobalSmoothApplied(toolId, true);
          },
          undo: () => {
            replaceShadedMesh(buildMesh(originalGeometry.clone()));
            get().setIsGlobalSmoothApplied(toolId, false);
          },
        },
      });
      return true;
    } catch (error) {
      console.error('[GlobalSmooth] smoothing failed', error);
      originalGeometry.dispose();
      return false;
    } finally {
      set({ isSmoothing: false });
    }
  },

  /** Hand the prepared command to the caller exactly once. */
  takePendingCommand: () => {
    const command = get().pendingCommand;
    set({ pendingCommand: null });
    return command;
  },

  reset: () =>
    set({
      intensity: DEFAULT_INTENSITY,
      lockVolume: true,
      isSmoothing: false,
      appliedByTool: {},
      appliedFromDraftByTool: {},
      commandByTool: {},
      pendingCommand: null,
    }),
}));

export default useGlobalSmoothStore;
